"use client";

import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, FileText, Wrench, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const steps = [{
  id: "consult",
  title: "Consult",
  description: "We assess your property needs and discuss your specific maintenance goals.",
  icon: MessageSquare
}, {
  id: "quote",
  title: "Quote",
  description: "Receive a transparent, comprehensive proposal tailored to your facility.",
  icon: FileText
}, {
  id: "execute",
  title: "Execute",
  description: "Our expert team delivers top-tier craftsmanship with minimal disruption.",
  icon: Wrench
}, {
  id: "maintain",
  title: "Maintain",
  description: "Ongoing, reliable care to keep your property pristine year-round.",
  icon: RefreshCw
}];
export default function PropertyCarePartnershipProcess() {
  const [progress, setProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const start = windowHeight * 0.8;
      const end = windowHeight * 0.3;
      let newProgress = 0;
      if (rect.top > start) {
        newProgress = 0;
      } else if (rect.top < end) {
        newProgress = 100;
      } else {
        newProgress = (start - rect.top) / (start - end) * 100;
      }
      setProgress(newProgress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="partnership-process" className="py-24 lg:py-40 bg-background relative overflow-hidden" ref={containerRef}>
      {}
      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold font-sans text-foreground mb-6 tracking-tight">
            Our Partnership Process
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            We've refined our approach into a clear, four-step journey designed to 
            take the stress out of property maintenance and deliver crystal-clear results.
          </p>
        </div>

        <div className="relative">
          {}
          <div className="absolute top-[60px] left-0 w-full h-1 bg-muted hidden lg:block rounded-full">
            <div className="h-full bg-primary transition-all duration-700 ease-out shadow-[0_0_20px_rgba(var(--primary),0.3)]" style={{
            width: `${progress}%`
          }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
            const Icon = step.icon;
            return <Card key={step.id} data-index={index} className="group relative border-border/50 bg-card/50 backdrop-blur-md hover:bg-card/80 transition-all duration-500 rounded-[24px] shadow-sm hover:shadow-xl hover:-translate-y-2">
                  <CardHeader className="pt-8 px-8 pb-4">
                    <div className="mb-6">
                      <Icon className="w-14 h-14 text-primary transition-transform duration-500 group-hover:scale-110" strokeWidth={1.25} aria-hidden="true" />
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-bold text-primary font-mono opacity-50">0{index + 1}</span>
                      <CardTitle className="text-2xl font-sans font-bold text-foreground">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-8 pb-8">
                    <CardDescription className="text-base text-muted-foreground leading-relaxed">
                      {step.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </section>;
}